import { Box, Button, Flex, Heading, List, ListItem, Tag, TagLabel, Text, useDisclosure } from '@chakra-ui/react';

import { trackEvent } from '~/features/analytics/track-event';

import { BrainIcon, ICON_SIZE } from '../icons';
import { pluralize } from '../utils';

import { SkillsModal } from './skills-modal';

export type ProgramSkillsSubsectionProps = {
  skills: string[];
  skillsCountTotal: number;
  skillsByCourse: Record<string, { courseTitle: string; skills: string[] }> | null;
  programKey: string;
  programTitle: string;
};

enum MaxSkillsToDisplay {
  MOBILE = 5,
  DESKTOP = 8,
}

export function ProgramSkillsSubsection({
  skills,
  skillsCountTotal,
  skillsByCourse,
  programKey,
  programTitle,
}: ProgramSkillsSubsectionProps) {
  const {
    isOpen: isSkillsModalOpen,
    onOpen: openSkillsModal,
    onClose: closeSkillsModal,
  } = useDisclosure({
    onOpen: () =>
      trackEvent({
        type: 'click',
        name: 'Product Page CTA Clicked',
        label: 'skills_clicked',
        additionalAttributes: {
          product_ids: [programKey],
        },
      }),
  });
  // Both breakpoints are rendered and toggled with CSS so the server markup matches the
  // first client paint. Resolving the count with a JS media query shifts the tag list once
  // the extra desktop tags wrap onto a new row after hydration.
  const hiddenSkillsCountMobile = Math.max(skillsCountTotal - MaxSkillsToDisplay.MOBILE, 0);
  const hiddenSkillsCountDesktop = Math.max(skillsCountTotal - MaxSkillsToDisplay.DESKTOP, 0);

  if (!skillsCountTotal) return null;

  return (
    <Flex as='section' flexDirection='column' gap={6} id='skills'>
      <Flex flexWrap='wrap' gap={2} justifyContent='space-between'>
        <Heading
          as='h2'
          size={{
            base: 'heading-6',
            md: 'heading-5',
          }}
        >
          Skills you&apos;ll learn
        </Heading>
        <Flex alignItems='center' gap={2}>
          <BrainIcon color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          <Text
            whiteSpace='nowrap'
            fontSize={{
              base: 'sm',
              md: 'md',
            }}
          >
            {pluralize(skillsCountTotal, 'skill', 'skills')}
          </Text>
        </Flex>
      </Flex>
      <Flex gap={4}>
        <List alignItems='center' display='flex' flexWrap='wrap' gap={2}>
          {skills.slice(0, MaxSkillsToDisplay.DESKTOP).map((skill, index) => (
            <Tag
              key={skill}
              as={ListItem}
              size='lg'
              display={
                index < MaxSkillsToDisplay.MOBILE
                  ? 'inline-flex'
                  : {
                      base: 'none',
                      md: 'inline-flex',
                    }
              }
            >
              <TagLabel textTransform='capitalize'>{skill}</TagLabel>
            </Tag>
          ))}
          {hiddenSkillsCountMobile > 0 && (
            <ListItem
              display={
                hiddenSkillsCountDesktop > 0
                  ? 'block'
                  : {
                      base: 'block',
                      md: 'none',
                    }
              }
            >
              <Button
                color='primary.blue'
                display='inline-block'
                padding={0}
                size='sm'
                textDecoration='underline'
                variant='link'
                onClick={openSkillsModal}
              >
                <Box
                  as='span'
                  display={{
                    base: 'inline',
                    md: 'none',
                  }}
                >
                  +{hiddenSkillsCountMobile} More
                </Box>
                {hiddenSkillsCountDesktop > 0 && (
                  <Box
                    as='span'
                    display={{
                      base: 'none',
                      md: 'inline',
                    }}
                  >
                    +{hiddenSkillsCountDesktop} More
                  </Box>
                )}
              </Button>
              <SkillsModal
                allSkills={skills}
                isOpen={isSkillsModalOpen}
                programTitle={programTitle}
                skillsByCourse={skillsByCourse}
                onClose={closeSkillsModal}
              />
            </ListItem>
          )}
        </List>
      </Flex>
    </Flex>
  );
}
